// Centralised humaniser for raw backend tool names. Used by both the
// in-flight SpinnerMessage rows (mid-stream "what is the agent doing?") and
// the post-stream ActionTrail "Sources:" footer. Keeping one mapping
// prevents the two surfaces from drifting apart and shows the user the
// same vocabulary in both states.

interface NamedStep {
  name: string;
  description?: string;
}

// Action-verb form, used for in-flight rows. Reads naturally with an
// ellipsis: "Looking up contact…", "Fetching resume…".
const PROGRESS_BY_PREFIX: Array<[string, string]> = [
  ['kula_search', 'Searching Kula'],
  ['kula_contact_get', 'Loading candidate record'],
  ['kula_contact_resumes', 'Parsing resume'],
  ['kula_contact_enrich', 'Enriching profile'],
  ['kula_contact_add_tags', 'Tagging candidate'],
  ['kula_contact_notes_create', 'Saving note'],
  ['kula_contact_notes_list', 'Loading notes'],
  ['kula_contact_create', 'Creating contact'],
  ['kula_contact_update', 'Updating contact'],
  ['kula_contact_delete', 'Removing contact'],
  ['kula_application_create', 'Adding to pipeline'],
  ['kula_application_move_stage', 'Moving stage'],
  ['kula_application_pause', 'Pausing application'],
  ['kula_application_unpause', 'Resuming application'],
  ['kula_application_transfer', 'Transferring application'],
  ['kula_job_match_candidates', 'Finding best-fit candidates'],
  ['kula_application_reject', 'Rejecting application'],
  ['kula_application_unreject', 'Restoring application'],
  ['kula_application_get', 'Loading application'],
  ['kula_application_list', 'Listing applications'],
  ['kula_application_notes_create', 'Saving application note'],
  ['kula_application_notes_list', 'Loading application notes'],
  ['kula_job_create', 'Creating job'],
  ['kula_job_update', 'Updating job'],
  ['kula_job_get', 'Loading job'],
  ['kula_job_close', 'Closing job'],
  ['kula_job_reopen', 'Reopening job'],
  ['kula_job_post_create', 'Publishing job post'],
  ['kula_job_post_update', 'Updating job post'],
  ['kula_job_description_generate', 'Drafting job description'],
  ['kula_job_ai_scoring_setup', 'Setting up AI scoring'],
  ['kula_job_ai_scoring', 'Scoring candidates'],
  ['kula_job_interview_plan_check', 'Resolving interview plan'],
  ['kula_job_stage_action_create', 'Adding stage activity'],
  ['kula_job_stage_', 'Updating pipeline stages'],
  ['kula_offer_send', 'Sending offer'],
  ['kula_email_draft', 'Drafting email'],
  ['kula_email_compose', 'Opening email composer'],
  ['kula_contact_send_email', 'Sending email'],
  ['kula_application_send_email', 'Sending email'],
  ['kula_interview_', 'Working on interview'],
  ['kula_flow_', 'Running outreach flow'],
  ['kula_report_', 'Building report'],
  ['kula_list_', 'Loading lookup'],
  ['propose_clarify', 'Waiting for your input'],
  ['delegate_to_agent', 'Asking a specialist'],
  ['consult_agent', 'Consulting a specialist'],
  ['web_search', 'Searching the web'],
  ['web_grounded_search', 'Researching with Google'],
  ['web_browse', 'Browsing a web page'],
  ['knowledge_search', 'Searching knowledge base'],
];

// Past-tense form, used for the ActionTrail footer. Reads as a completed
// action: "Searched Kula", "Loaded resume".
const PAST_BY_PREFIX: Array<[string, string]> = [
  ['kula_search', 'Searched Kula'],
  ['kula_contact_get', 'Loaded candidate'],
  ['kula_application_pause', 'Paused application'],
  ['kula_application_unpause', 'Resumed application'],
  ['kula_application_transfer', 'Transferred application'],
  ['kula_job_match_candidates', 'Matched candidates'],
  ['kula_email_draft', 'Drafted email'],
  ['kula_email_compose', 'Opened email composer'],
  ['kula_contact_send_email', 'Sent email'],
  ['kula_application_send_email', 'Sent email'],
  ['kula_contact_resumes', 'Loaded resume'],
  ['kula_contact_enrich', 'Enriched profile'],
  ['kula_contact_add_tags', 'Tagged candidate'],
  ['kula_contact_notes_create', 'Saved note'],
  ['kula_application_create', 'Added to pipeline'],
  ['kula_application_move_stage', 'Moved stage'],
  ['kula_application_reject', 'Rejected'],
  ['kula_job_create', 'Created job'],
  ['kula_job_get', 'Loaded job'],
  ['kula_job_ai_scoring_setup', 'AI scoring configured'],
  ['kula_job_interview_plan_check', 'Interview plan resolved'],
  ['propose_clarify', 'Asked for a choice'],
  ['delegate_to_agent', 'Delegated to specialist'],
  ['consult_agent', 'Consulted specialist'],
  ['web_search', 'Searched the web'],
  ['web_grounded_search', 'Web research'],
  ['knowledge_search', 'Searched knowledge'],
];

const lookup = (
  table: Array<[string, string]>,
  name: string
): string | null => {
  for (const [prefix, label] of table) {
    if (name === prefix || name.startsWith(prefix)) return label;
  }
  return null;
};

// Last-resort prettification when we don't have a specific mapping.
// "kula_foo_bar" → "Foo bar". Capitalised so an unmapped (or brand-new) tool
// still reads as a label, not a raw lowercase token — keeps the timeline tidy
// without needing a map entry for every tool.
const stripPrefix = (name: string): string => {
  const words = name
    .replace(/^kula_/, '')
    .replace(/_/g, ' ')
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : words;
};

/** In-flight verb form. "Loading candidate record" style. */
export const toolProgressLabel = (step: NamedStep): string => {
  const explicit = step.description?.trim();
  if (explicit) return explicit;
  return lookup(PROGRESS_BY_PREFIX, step.name) ?? stripPrefix(step.name);
};

/** Past-tense form for the post-stream Sources footer. */
export const toolPastLabel = (step: NamedStep): string => {
  if (step.description && step.description.trim().length > 0) {
    return step.description;
  }
  return lookup(PAST_BY_PREFIX, step.name) ?? stripPrefix(step.name);
};

// Picked once at module load so a re-render mid-stream can't churn the
// fallback label. Claude-Code-style gerund pool from
// `threadContainer/constants/claudeThinkingStates`.
import { claudeThinkingStates } from '../threadContainer/constants';

const DEFAULT_FALLBACK =
  claudeThinkingStates[
    Math.floor(Math.random() * claudeThinkingStates.length)
  ] ?? 'Thinking';

/** Contextual top-of-spinner label derived from the most recent sub-step.
 *  When no sub-step has been pushed yet (the moment between user-submit
 *  and first tool_start), falls back to a Claude-Code-style gerund —
 *  better vibe than a flat "Thinking" while still honest about state. */
export const contextualThinkingLabel = (
  subSteps: NamedStep[] | undefined
): string => {
  if (!subSteps || subSteps.length === 0) return DEFAULT_FALLBACK;
  const last = subSteps[subSteps.length - 1];
  if (!last) return DEFAULT_FALLBACK;
  return toolProgressLabel(last);
};
